/**
 * VariantPickerDrawer -- Full variant manager for a single career entry.
 *
 * Opens as a slide-in drawer (matching the TailorPanel pattern) and allows:
 *   - Viewing all variants with label and responsibility preview
 *   - Activating a variant (or switching back to base)
 *   - Deleting a variant (disabled when active; shows error on 409)
 *
 * Accessibility:
 *   - role="dialog", aria-modal, labelled by heading
 *   - Esc closes the drawer
 *   - Focus trapped inside; initial focus on close button
 *   - aria-live region for status announcements
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Button } from '../ui/Button';
import { careerApi } from '../../services/cms.api';
import { cmsSitesApi } from '../../services/cms.api';
import type { CareerEntryVariant, SiteCareerEntry } from '../../types/cms.types';
import { logger } from '../../services/logger.client';

const log = logger.child({ module: 'VariantPickerDrawer' });

/** Maximum number of responsibility bullets to preview. */
const PREVIEW_BULLETS = 2;

interface VariantPickerDrawerProps {
  siteId: string;
  entryId: string;
  entryLabel: string;
  currentVariantId: string | null;
  open: boolean;
  onClose: () => void;
  onVariantChanged: () => void;
}

type DrawerStatus = 'loading' | 'ready' | 'error' | 'switching' | 'deleting';

export function VariantPickerDrawer({
  siteId,
  entryId,
  entryLabel,
  currentVariantId,
  open,
  onClose,
  onVariantChanged,
}: VariantPickerDrawerProps) {
  const [variants, setVariants] = useState<CareerEntryVariant[]>([]);
  const [status, setStatus] = useState<DrawerStatus>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeVariantId, setActiveVariantId] = useState<string | null>(currentVariantId);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  /* ---- Load variants on open ---- */
  useEffect(() => {
    if (!open) return;
    setActiveVariantId(currentVariantId);
    setErrorMsg(null);
    setStatus('loading');
    setLiveAnnouncement('Loading variants...');

    careerApi
      .listVariants(entryId)
      .then((data) => {
        setVariants(data);
        setStatus('ready');
        setLiveAnnouncement(
          data.length === 0
            ? 'No variants available.'
            : `${data.length} variant${data.length === 1 ? '' : 's'} loaded.`,
        );
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Failed to load variants';
        log.error('Failed to load variants', { entryId, error: msg });
        setErrorMsg(msg);
        setStatus('error');
        setLiveAnnouncement(`Error: ${msg}`);
      });
  }, [open, entryId, currentVariantId]);

  /* ---- Focus management + scroll lock ---- */
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    setTimeout(() => closeBtnRef.current?.focus(), 0);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  /* ---- Focus trap + Esc close ---- */
  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && drawerRef.current) {
        const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  /* ---- Switch active variant ---- */
  const handleActivate = useCallback(
    async (variantId: string | null) => {
      setStatus('switching');
      setErrorMsg(null);
      const label = variantId
        ? variants.find((v) => v.id === variantId)?.label ?? 'variant'
        : 'base';
      setLiveAnnouncement(`Switching to ${label}...`);

      try {
        /* Fetch the current site career entries, update the matching one,
           then PUT the full array back -- matching existing pattern. */
        const siteEntries = await cmsSitesApi.getCareerEntries(siteId);
        const updated = siteEntries.map((e: SiteCareerEntry) =>
          e.career_entry_id === entryId ? { ...e, variant_id: variantId } : e,
        );
        await cmsSitesApi.updateCareerEntries(siteId, updated);
        setActiveVariantId(variantId);
        setStatus('ready');
        setLiveAnnouncement(`Switched to ${label}.`);
        onVariantChanged();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to switch variant';
        log.error('Failed to switch variant', { entryId, variantId, error: msg });
        setErrorMsg(msg);
        setStatus('ready');
        setLiveAnnouncement(`Error: ${msg}`);
      }
    },
    [siteId, entryId, variants, onVariantChanged],
  );

  /* ---- Delete variant ---- */
  const handleDelete = useCallback(
    async (variantId: string) => {
      const variantLabel = variants.find((v) => v.id === variantId)?.label ?? 'variant';
      if (!window.confirm(`Delete variant "${variantLabel}"? This cannot be undone.`)) {
        return;
      }

      setStatus('deleting');
      setErrorMsg(null);
      setLiveAnnouncement(`Deleting ${variantLabel}...`);

      try {
        await careerApi.deleteVariant(entryId, variantId);
        setVariants((prev) => prev.filter((v) => v.id !== variantId));
        setStatus('ready');
        setLiveAnnouncement(`Deleted ${variantLabel}.`);
        onVariantChanged();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete variant';
        /* 409 means variant is active on another site */
        const is409 = msg.includes('409') || msg.toLowerCase().includes('conflict') || msg.toLowerCase().includes('active');
        const displayMsg = is409
          ? `Cannot delete "${variantLabel}" because it is currently active on another site.`
          : msg;
        log.error('Failed to delete variant', { entryId, variantId, error: msg });
        setErrorMsg(displayMsg);
        setStatus('ready');
        setLiveAnnouncement(`Error: ${displayMsg}`);
      }
    },
    [entryId, variants, onVariantChanged],
  );

  if (!open) return null;

  const isBusy = status === 'switching' || status === 'deleting';

  return (
    <div
      className="variant-picker__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={drawerRef}
        className="variant-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="variant-picker-title"
        onKeyDown={handleKeyDown}
      >
        {/* ---- Header ---- */}
        <div className="variant-picker__header">
          <div className="variant-picker__header-text">
            <h2 id="variant-picker-title" className="variant-picker__title">
              Variants
            </h2>
            <p className="variant-picker__subtitle">{entryLabel}</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="variant-picker__close"
            onClick={onClose}
            aria-label="Close variant picker"
          >
            &times;
          </button>
        </div>

        {/* ---- Body ---- */}
        <div className="variant-picker__body">
          {/* SR-only live region */}
          <div
            className="variant-picker__live"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {liveAnnouncement}
          </div>

          {/* Error banner */}
          {errorMsg && (
            <div className="variant-picker__error" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Loading state */}
          {status === 'loading' && (
            <div className="variant-picker__loading">
              <p>Loading variants...</p>
            </div>
          )}

          {/* Ready / switching / deleting states */}
          {status !== 'loading' && status !== 'error' && (
            <>
              {/* Base (original) option */}
              <button
                type="button"
                className={`variant-picker__item ${activeVariantId === null ? 'variant-picker__item--active' : ''}`}
                onClick={() => {
                  if (activeVariantId !== null) void handleActivate(null);
                }}
                disabled={activeVariantId === null || isBusy}
                aria-current={activeVariantId === null ? 'true' : undefined}
              >
                <div className="variant-picker__item-content">
                  <div className="variant-picker__item-header">
                    <span className="variant-picker__item-label">Base (original)</span>
                    {activeVariantId === null && (
                      <span className="variant-picker__active-badge" aria-label="Currently active">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="variant-picker__item-desc">
                    Uses the original description and responsibilities.
                  </p>
                </div>
              </button>

              {/* Variant list */}
              {variants.length === 0 && (
                <div className="variant-picker__empty">
                  <p>No variants yet.</p>
                  <p className="variant-picker__empty-hint">
                    Variants are created when you tailor this entry for a target role.
                  </p>
                </div>
              )}

              {variants.map((v) => {
                const isActive = v.id === activeVariantId;
                const bullets = v.responsibilities ?? [];
                const previewBullets = bullets.slice(0, PREVIEW_BULLETS);
                const remaining = bullets.length - PREVIEW_BULLETS;

                return (
                  <div
                    key={v.id}
                    className={`variant-picker__item ${isActive ? 'variant-picker__item--active' : ''}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <div className="variant-picker__item-content">
                      <div className="variant-picker__item-header">
                        <span className="variant-picker__item-label">{v.label}</span>
                        {isActive && (
                          <span className="variant-picker__active-badge" aria-label="Currently active">
                            Active
                          </span>
                        )}
                      </div>

                      {v.description && (
                        <p className="variant-picker__item-desc">{v.description}</p>
                      )}

                      {previewBullets.length > 0 && (
                        <ul className="variant-picker__item-bullets">
                          {previewBullets.map((r, i) => (
                            <li key={i}>{r}</li>
                          ))}
                          {remaining > 0 && (
                            <li className="variant-picker__item-more">
                              +{remaining} more
                            </li>
                          )}
                        </ul>
                      )}
                    </div>

                    <div className="variant-picker__item-actions">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleActivate(v.id)}
                        disabled={isActive || isBusy}
                        aria-label={`Activate ${v.label}`}
                      >
                        Activate
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => void handleDelete(v.id)}
                        disabled={isActive || isBusy}
                        aria-label={`Delete ${v.label}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Error-only state (failed to load) */}
          {status === 'error' && variants.length === 0 && (
            <div className="variant-picker__error-state">
              <p>Could not load variants.</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setStatus('loading');
                  setErrorMsg(null);
                  careerApi
                    .listVariants(entryId)
                    .then((data) => {
                      setVariants(data);
                      setStatus('ready');
                    })
                    .catch((err) => {
                      setErrorMsg(err instanceof Error ? err.message : 'Failed to load');
                      setStatus('error');
                    });
                }}
              >
                Retry
              </Button>
            </div>
          )}
        </div>

        {/* ---- Footer ---- */}
        <div className="variant-picker__footer">
          <Button variant="secondary" onClick={onClose} disabled={isBusy}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
